// The page's side of the Revise room's worker (`reviseHost.ts`): one per open project, started on the first request
// and ended after two idle minutes. Before each request it sends the documents that changed since the worker last had
// them (the changes since, or the whole document when its history was replaced), as Publish does, so the page's own
// work is a few small saves — History's diff and the style check never hold up typing. Where no worker can start (a
// build without one, tests) the same host runs in this thread instead.
import * as A from '@automerge/automerge';
import type { ProjectDiff } from '@writing-suite/core';
import type { BuiltInStyleCheck, DashStyle, SpellingDictionary, StyleFinding } from '@writing-suite/core/revise';
import { startReviseWorker } from '../publish/client.ts';
import {
  receivedDiff, ReviseHost, type AttributedDiff, type DictionaryLoader, type DocUpdate, type FromHost, type Port, type ReviseRequest, type SentBook, type SentDiff,
  type ToHost,
} from './reviseHost.ts';

export interface ReviseConnection {
  port: Port<FromHost, ToHost>;
  terminate(): void;
  /** Called when the worker fails (its script couldn't load): the client carries on in this thread. */
  onFail?: ((why: string) => void) | null;
}

/** The host in this thread, answering after the current task (as a worker would). */
export function memoryConnection(loadDictionary?: DictionaryLoader): ReviseConnection {
  let toPage: (m: FromHost) => void = () => {};
  let toHost: (m: ToHost) => void = () => {};
  new ReviseHost({ post: (m) => setTimeout(() => toPage(m), 0), listen: (fn) => (toHost = fn) }, loadDictionary);
  return {
    port: { post: (m) => setTimeout(() => toHost(m), 0), listen: (fn) => (toPage = fn) },
    terminate: () => {
      toPage = () => {};
      toHost = () => {};
    },
  };
}

/** A module worker, or null when this build has none. */
export function workerConnection(): ReviseConnection | null {
  let worker: Worker | null;
  try {
    worker = startReviseWorker();
  } catch {
    return null;
  }
  if (!worker) return null;
  const w = worker;
  const connection: ReviseConnection = {
    port: {
      post: (message, transfer) => w.postMessage(message, transfer ?? []),
      listen: (fn) => {
        w.onmessage = (e: MessageEvent<FromHost>) => fn(e.data);
      },
    },
    terminate: () => w.terminate(),
    onFail: null,
  };
  w.onerror = (e) => {
    e.preventDefault();
    connection.onFail?.(e.message || 'the worker didn’t start');
  };
  return connection;
}

const IDLE_MS = 120_000;

interface Pending { request: ReviseRequest; resolve(v: unknown): void; reject(e: Error): void }

export class ReviseClient {
  private connection: ReviseConnection | null = null;
  private sent = new Map<string, A.Heads>();
  private pending = new Map<number, Pending>();
  private nextId = 1;
  private idle: ReturnType<typeof setTimeout> | null = null;
  private inThread = false;
  /** Requests answered (tests). */
  answered = 0;

  constructor(
    private readonly documents: () => Map<string, A.Doc<unknown>>,
    private readonly connect: () => ReviseConnection | null = workerConnection,
  ) {}

  /** Whether the work runs in this thread (no worker could start). */
  get runsInThread(): boolean {
    return this.inThread;
  }

  private open(): ReviseConnection {
    if (this.connection) return this.connection;
    let c = this.inThread ? null : this.connect();
    if (!c) {
      this.inThread = true;
      c = memoryConnection();
    }
    c.port.listen((m) => this.receive(m));
    c.onFail = () => this.fallBack();
    this.connection = c;
    this.sent.clear();
    return c;
  }

  /** The worker couldn't start: the same host in this thread, the requests waiting asked again. */
  private fallBack() {
    this.connection?.terminate();
    this.connection = null;
    this.inThread = true;
    const waiting = [...this.pending.entries()];
    this.pending.clear();
    for (const [, p] of waiting) void this.request(p.request).then(p.resolve, p.reject);
  }

  /** Sends the host what changed in the documents since it last had them. */
  private sync(c: ReviseConnection) {
    const docs = this.documents();
    const updates: DocUpdate[] = [];
    for (const [name, doc] of docs) {
      const heads = A.getHeads(doc);
      const had = this.sent.get(name);
      if (had && had.length === heads.length && had.every((h, i) => h === heads[i])) continue;
      const incremental = had !== undefined && A.hasHeads(doc, had);
      updates.push({ name, bytes: incremental ? A.saveSince(doc, had) : A.save(doc), full: !incremental });
      this.sent.set(name, heads);
    }
    const removed = [...this.sent.keys()].filter((n) => !docs.has(n));
    for (const n of removed) this.sent.delete(n);
    if (updates.length || removed.length) c.port.post({ t: 'sync', updates, removed });
  }

  private request<T>(request: ReviseRequest): Promise<T> {
    if (this.idle) clearTimeout(this.idle);
    let c: ReviseConnection;
    try {
      c = this.open();
      this.sync(c);
    } catch (e) {
      return Promise.reject(e);
    }
    const id = this.nextId++;
    return new Promise<T>((resolve, reject) => {
      this.pending.set(id, { request, resolve: resolve as (v: unknown) => void, reject });
      c.port.post({ t: 'request', id, request });
    });
  }

  private receive(m: FromHost) {
    const p = this.pending.get(m.id);
    if (!p) return;
    this.pending.delete(m.id);
    this.answered++;
    if (m.ok) p.resolve(m.result);
    else p.reject(new Error(m.error));
    if (!this.pending.size) {
      if (this.idle) clearTimeout(this.idle);
      this.idle = setTimeout(() => this.stop(), IDLE_MS);
    }
  }

  /** History's comparison: a checkpoint against a later one, or today's draft (`to` null). */
  async history(from: string, to: string | null, arrangement: string | null): Promise<AttributedDiff> {
    return receivedDiff(await this.request<SentDiff>({ kind: 'history', from, to, arrangement }));
  }

  /** The switched-on built-in checks over the manuscript. */
  style(checks: BuiltInStyleCheck[], names: string[]): Promise<StyleFinding[]> {
    return this.request<StyleFinding[]>({ kind: 'style', checks, names });
  }

  /** Revise ▸ Checks' whole manuscript: every unit's scenes and what to underline in them (spelling too, with a
   *  dictionary). */
  book(styleFindings: StyleFinding[] | null, dismissed: string[], dashStyle: DashStyle | null,
    spelling: { dictionary: SpellingDictionary; known: string[] } | null = null): Promise<SentBook> {
    return this.request<SentBook>({ kind: 'book', styleFindings, dismissed, dashStyle, spelling });
  }

  /** Ends the worker (the next request starts another and sends it everything again). */
  stop() {
    if (this.idle) clearTimeout(this.idle);
    this.idle = null;
    this.connection?.terminate();
    this.connection = null;
    for (const p of this.pending.values()) p.reject(new Error('Stopped.'));
    this.pending.clear();
  }
}

const clients = new WeakMap<object, ReviseClient>();

/** The open project's client, shared by its pages (History, Checks). */
export function reviseClient(live: { documents(): Map<string, A.Doc<unknown>> }): ReviseClient {
  let c = clients.get(live);
  if (!c) {
    c = new ReviseClient(() => live.documents());
    clients.set(live, c);
  }
  return c;
}
